import { useHistory } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { Divider, Form } from 'antd';

import { PageProps } from '@/types/PageProps';

import TabContentWrapper from '@/components/_common/TabContentWrapper';
import { SaveButton } from '@/components/_common/Buttons';
import DirectSaleForm from '@/components/Movements/DirectSale/DirectSaleForm';
import { useCreateDirectSale } from '@/core/domain/sales/DirectSale/DirectSale.hooks';
import {
  CreateDirectSaleRequest,
  DirectSaleFormValues,
} from '@/core/domain/sales/DirectSale/DirectSale.types';
import Authorities from '@/types/Authority';
import ButtonBack from '@/components/_common/ButtonBack';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import { DateTimeFormat } from '@/utils/moment';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import useSession from '@/hooks/useSession';
import { useEffect } from 'react';

function DirectSaleCreatePage({ route }: PageProps) {
  const history = useHistory();
  const session = useSession();

  const [form] = Form.useForm<DirectSaleFormValues>();
  const employeeIdField = Form.useWatch('employeeId', form);

  const mutation = useCreateDirectSale();

  const hasLinkSellerToLoggedUser = hasPermissions([
    Authorities.MOVIMENTATION_SALE_LINK_SELLER_TO_LOGGED_USER,
  ]);

  useEffect(() => {
    if (hasLinkSellerToLoggedUser && session && !employeeIdField) {
      form.setFieldValue('employeeId', session.employeeId);
    }
  }, [session, hasLinkSellerToLoggedUser, employeeIdField]);

  const handleSaveClick = async () => {
    try {
      const { items, ...values } = await form.validateFields();

      const obj: CreateDirectSaleRequest = {
        ...values,
        items: items.map((item) => ({
          discount: Number(item.discount / 100),
          partId: item.partId,
          quantity: item.totalStock,
          unitPrice: item.unitaryValue,
          unitCost: item.unitCost,
          manufacturerId: item?.manufacturerId,
        })),
        note: values.note ?? '',
        saleDate: values.saleDate.format(DateTimeFormat),
      };

      const response = await mutation.mutateAsync(obj);

      history.push(`/app/movements/directSale/${response.id}`);
    } catch {}
  };

  return (
    <TabContentWrapper
      title={route.name}
      extra={
        <SaveButton
          type="primary"
          onClick={handleSaveClick}
          loading={mutation.isLoading}
          disabled={!hasPermissions([Authorities.MOVIMENTATION_SALE_ADD])}
          block
        >
          <FormattedMessage id="commons.form.button.save" />
        </SaveButton>
      }
    >
      <DirectSaleForm form={form} />

      <Divider />

      <SpaceForButton>
        <ButtonBack path="/app/movements/directSale" />
      </SpaceForButton>
    </TabContentWrapper>
  );
}

export default DirectSaleCreatePage;
